import { useCallback, useMemo } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, IdCard, ShieldAlert } from 'lucide-react'
import { useT } from '@/i18n'
import type { Catalog } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams, RowAction } from '@/components/DataTable/types'
import { fetchReportsByCategory, type AdminReport } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/** TZ A6.03–08 ⇄ report kategoriyasi + i18n yorlig'i + ekran ID'si. */
const CATEGORY_META: Record<string, { screenId: string; label: (t: Catalog) => string }> = {
  csam: { screenId: 'A6.03', label: (t) => t.mod.catCsam },
  violence: { screenId: 'A6.04', label: (t) => t.mod.catViolence },
  spam: { screenId: 'A6.05', label: (t) => t.mod.catSpam },
  fraud: { screenId: 'A6.06', label: (t) => t.mod.catFraud },
  copyright: { screenId: 'A6.07', label: (t) => t.mod.catCopyright },
  doxxing: { screenId: 'A6.08', label: (t) => t.mod.catDoxxing },
}

/**
 * A6.03–08 — Kategoriya bo'yicha izolyatsiyalangan moderatsiya navbati (dizayn
 * §8: CSAM va boshqalar alohida oqim). Bitta parametrli ekran — route'dagi
 * `:category` ni server `?category=` filtriga uzatadi. Backend `4f05814`.
 */
export function CategoryQueue() {
  const t = useT()
  const navigate = useNavigate()
  const { category = '' } = useParams()
  const meta = CATEGORY_META[category]

  const rowActions = useMemo<RowAction<AdminReport>[]>(
    () => [
      {
        id: 'card',
        label: t.users.viewCard,
        icon: <IdCard size={13} />,
        onSelect: (r) => navigate(`/moderation/${r.id}`),
      },
    ],
    [t, navigate],
  )

  const columns = useMemo<AdminColumn<AdminReport>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 70 },
      { key: 'created_at', header: t.col.time, type: 'date', width: 150 },
      {
        key: 'target_type',
        header: t.mod.target,
        type: 'text',
        width: 160,
        sortable: false,
        render: (r) => (
          <span className="font-mono text-xs">
            {r.target_type}#{r.target_id}
          </span>
        ),
      },
      {
        key: 'reporter_id',
        header: t.mod.reporter,
        type: 'text',
        width: 150,
        sortable: false,
        render: (r) => (
          <Link to={`/users/${r.reporter_id}`} className="text-accent hover:underline">
            {r.reporter_username ? `@${r.reporter_username}` : `#${r.reporter_id}`}
          </Link>
        ),
      },
      {
        key: 'status',
        header: t.col.status,
        type: 'enum',
        width: 130,
        options: [
          { value: 'pending', label: t.mod.pending, status: 'pending' },
          { value: 'resolved', label: t.mod.resolved, status: 'active' },
          { value: 'dismissed', label: t.mod.dismissed, status: 'deleted' },
        ],
      },
      { key: 'reason', header: t.mod.reason, type: 'longtext', minWidth: 200 },
    ],
    [t],
  )

  const fetcher = useCallback((params: FetchParams) => fetchReportsByCategory(category, params), [category])

  // Noma'lum kategoriya — umumiy navbatga qaytaramiz.
  if (!meta) return <Navigate to="/moderation" replace />

  return (
    <>
      <PageHeader
        title={`${meta.label(t)} — ${t.mod.queue}`}
        screenId={meta.screenId}
        actions={
          <Link
            to="/moderation"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.mod.queue}
          </Link>
        }
      />
      <DataTable<AdminReport>
        screenId={meta.screenId}
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <ShieldAlert size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
